import { useState, useEffect } from 'react';
import CreateInviteModal from './CreateInviteModal';
import AcceptInviteModal from './AcceptInviteModal';
import CreatedInvitesTable from './CreatedInvitesTable';
import AcceptedInvitesTable from './AcceptedInvitesTable';
import useInviteActions from './useInviteActions';
import styles from './Invites.module.css';

/**
 * Invites Component
 *
 * Displays budget invitation management:
 * - Create invite button (for budget owners only - across all budgets they own)
 * - Accept invite button (for all users - not role-specific)
 * - Table of invites created by user (only shown if user owns any budgets)
 *   - Shows invite status: Pending, Active, Ceased, Deactivated, Expired
 *   - Deactivate button for pending invites (not yet accepted)
 *   - Remove Access button for active members (cease/remove their access)
 * - Table of invites accepted by user (for all users - not role-specific)
 *
 * Props:
 * - userEmail (string): Current user's email
 * - budgets (array): User's accessible budgets
 * - onInviteAccepted (function): Called when user accepts an invite to refresh budget list
 * - getAccessTokenSilently (function): Auth0 function to get access token
 * - userRole (string): User's role in current budget (not used - invite permissions are based on ownership across all budgets)
 */
function Invites({ userEmail, budgets, onInviteAccepted, getAccessTokenSilently, _userRole }) {
    const [createdInvites, setCreatedInvites] = useState([]);
    const [acceptedInvites, setAcceptedInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAcceptModal, setShowAcceptModal] = useState(false);

    // Filter budgets where user is owner
    const ownedBudgets = budgets.filter(b => b.role === 'owner');

    const fetchInvites = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_BASE_URL}/invites`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch invites');
            }

            const data = await response.json();
            setCreatedInvites(data.createdInvites || []);
            setAcceptedInvites(data.acceptedInvites || []);
        } catch (err) {
            // Error fetching invites
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const { handleDeactivate, handleRemoveAccess } = useInviteActions(getAccessTokenSilently, fetchInvites);

    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        fetchInvites();
    };

    const handleAcceptSuccess = (budgetInfo) => {
        setShowAcceptModal(false);
        fetchInvites();
        if (onInviteAccepted) {
            onInviteAccepted(budgetInfo); // Refresh budgets list and switch to new budget
        }
    };

    useEffect(() => {
        fetchInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);

    if (loading) {
        return (
            <div className={styles.loading}>
                <p>Loading invites...</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Action Buttons */}
            <div className={styles.actionButtons}>
                {ownedBudgets.length > 0 && (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className={styles.createButton}
                    >
                        Create Invite
                    </button>
                )}
                <button
                    onClick={() => setShowAcceptModal(true)}
                    className={styles.acceptButton}
                >
                    Accept Invite
                </button>
            </div>

            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            {/* Created Invites Table */}
            <CreatedInvitesTable
                createdInvites={createdInvites}
                handleDeactivate={handleDeactivate}
                handleRemoveAccess={handleRemoveAccess}
            />

            {/* Accepted Invites Table */}
            <AcceptedInvitesTable acceptedInvites={acceptedInvites} />

            {/* Empty State */}
            {createdInvites.length === 0 && acceptedInvites.length === 0 && (
                <div className={styles.emptyState}>
                    <p>No invites yet. Create an invite or accept one to get started.</p>
                </div>
            )}

            {/* Modals */}
            {showCreateModal && (
                <CreateInviteModal
                    userEmail={userEmail}
                    budgets={ownedBudgets}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={handleCreateSuccess}
                    getAccessTokenSilently={getAccessTokenSilently}
                />
            )}

            {showAcceptModal && (
                <AcceptInviteModal
                    userEmail={userEmail}
                    onClose={() => setShowAcceptModal(false)}
                    onSuccess={handleAcceptSuccess}
                    getAccessTokenSilently={getAccessTokenSilently}
                />
            )}
        </div>
    );
}

export default Invites;
